import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";

export type VoucherStatus = "active" | "redeemed" | "expired" | "cancelled";

export interface PartnerLite {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
}

export interface InventoryItem {
  id: string;
  partner_id: string;
  sku: string;
  name: string;
  description: string | null;
  cost_points: number;
  stock: number | null;
  expires_at: string | null;
  is_active: boolean;
}

export interface Voucher {
  id: string;
  session_id: string;
  inventory_id: string | null;
  partner_id: string | null;
  code: string;
  qr_payload: string;
  status: VoucherStatus;
  issued_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  metadata: Record<string, unknown> | null;
  recipient_email?: string | null;
  delivery_email_sent_at?: string | null;
  delivery_email_resent_count?: number | null;
  delivery_whatsapp_sent_at?: string | null;
  delivery_pdf_downloaded_at?: string | null;
  cancelled_at?: string | null;
  cancelled_reason?: string | null;
  redeemed_location?: Record<string, unknown> | null;
  partner?: PartnerLite | null;
  inventory?: InventoryItem | null;
}

const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // crockford-ish, no I/O/0/1

function generateCode(len = 8): string {
  let s = "";
  for (let i = 0; i < len; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return s.replace(/(.{4})/, "$1-");
}

/**
 * Issues a real voucher backed by reward_inventory. Returns the voucher row
 * (including QR payload) on success, or null on failure.
 */
export async function generateVoucher(opts: {
  inventoryId?: string | null;
  partnerId?: string | null;
  rewardName: string;
  rewardCode: string;
  ttlDays?: number;
  recipientEmail?: string | null;
}): Promise<Voucher | null> {
  const sessionId = getSessionId();
  const code = generateCode(8);
  const ttl = opts.ttlDays ?? 30;
  const expiresAt = new Date(Date.now() + ttl * 24 * 60 * 60 * 1000).toISOString();
  const qrPayload = `SS2026:${code}:${opts.partnerId ?? "x"}:${expiresAt.slice(0, 10)}`;
  const { data, error } = await supabase
    .from("reward_vouchers")
    .insert({
      session_id: sessionId,
      inventory_id: opts.inventoryId ?? null,
      partner_id: opts.partnerId ?? null,
      code,
      qr_payload: qrPayload,
      status: "active",
      expires_at: expiresAt,
      recipient_email: opts.recipientEmail ?? null,
      metadata: { reward_name: opts.rewardName, reward_code: opts.rewardCode },
    })
    .select()
    .single();
  if (error || !data) {
    console.warn("[voucher] insert failed", error);
    return null;
  }
  void trackEvent("reward_voucher_generated", {
    code,
    reward: opts.rewardName,
    partner_id: opts.partnerId ?? null,
    expires_at: expiresAt,
  });
  // Best-effort auto email delivery
  if (opts.recipientEmail) {
    try {
      const { sendVoucherEmail } = await import("./voucherDelivery");
      void sendVoucherEmail(data as Voucher, { isResend: false });
    } catch {
      /* delivery layer not ready */
    }
  }
  return data as Voucher;
}

export async function getMyVouchers(): Promise<Voucher[]> {
  const sessionId = getSessionId();
  const { data } = await supabase
    .from("reward_vouchers")
    .select("*, partner:reward_partners(id,slug,name,logo_url), inventory:reward_inventory(*)")
    .eq("session_id", sessionId)
    .order("issued_at", { ascending: false })
    .limit(100);
  // Auto-mark expired client-side for stale rows
  const now = Date.now();
  return ((data as Voucher[]) ?? []).map((v) => {
    if (v.status === "active" && v.expires_at && new Date(v.expires_at).getTime() < now) {
      void supabase.from("reward_vouchers").update({ status: "expired" }).eq("id", v.id);
      void trackEvent("reward_voucher_expired", { code: v.code });
      return { ...v, status: "expired" as VoucherStatus };
    }
    return v;
  });
}

export async function markVoucherRedeemed(code: string, redeemedBy = "self-demo") {
  const { error } = await supabase
    .from("reward_vouchers")
    .update({ status: "redeemed", redeemed_at: new Date().toISOString(), redeemed_by: redeemedBy })
    .eq("code", code);
  if (!error) void trackEvent("reward_voucher_redeemed", { code, by: redeemedBy });
  return !error;
}

export async function listPartners(): Promise<PartnerLite[]> {
  const { data } = await supabase
    .from("reward_partners")
    .select("id,slug,name,logo_url")
    .eq("is_active", true);
  return (data as PartnerLite[]) ?? [];
}

export async function listInventory(): Promise<InventoryItem[]> {
  const { data } = await supabase
    .from("reward_inventory")
    .select("*")
    .eq("is_active", true)
    .order("cost_points", { ascending: true });
  return (data as InventoryItem[]) ?? [];
}